import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { PiDownloadSimpleBold, PiEyeBold, PiFileTextBold } from "react-icons/pi";
import client from "../../api/client";
import { downloadSubmissionFile } from "../../utils/submissionFile";

/**
 * Shared submission/assignment file viewer. Renders an inline preview so
 * instructors and students don't have to download a file just to see it.
 *
 * Two mutually-exclusive ways to use it:
 *  - `url`: a plain, publicly reachable link (e.g. a pasted Google Drive
 *    link). Fetched directly / embedded directly in the browser.
 *  - `submission`: an uploaded file served only through the authenticated,
 *    forced-download API route (`submission.file_download_url`). Fetched via
 *    the authenticated axios client as a blob, then rendered from a local
 *    blob: URL — this keeps the "never expose a raw /storage URL" hardening
 *    intact while still giving a real inline preview.
 */

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"];
const PDF_EXTENSIONS = ["pdf"];
const OFFICE_DOC_EXTENSIONS = ["doc", "docx", "ppt", "pptx"];
const SPREADSHEET_EXTENSIONS = ["xls", "xlsx"];
const CSV_EXTENSIONS = ["csv"];
const TEXT_EXTENSIONS = ["txt", "md", "log", "json"];

function getFileExtension(name) {
  if (!name) return "";
  const clean = name.split(/[?#]/)[0];
  const parts = clean.split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

function getFileName(url) {
  if (!url) return "attachment";
  try {
    const clean = url.split(/[?#]/)[0];
    return decodeURIComponent(clean.split("/").pop());
  } catch {
    return "attachment";
  }
}

// Minimal RFC 4180 parser: handles quoted fields, escaped quotes ("") and
// commas/newlines inside quotes. Good enough for instructor-facing preview.
function parseCSV(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      row.push(field);
      field = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      field = "";
      rows.push(row);
      row = [];
    } else {
      field += char;
    }
  }
  if (field.length || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => !(r.length === 1 && r[0] === ""));
}

function GridTable({ rows, tall }) {
  if (!rows.length) {
    return <div className="p-4 text-xs text-gray-400 italic">Empty file.</div>;
  }
  const capped = rows.slice(0, 200);
  return (
    <div className={`${tall ? "max-h-[75vh]" : "max-h-72"} overflow-auto`}>
      <table className="w-full text-xs border-collapse">
        <tbody>
          {capped.map((row, ri) => (
            <tr key={ri} className={ri === 0 ? "bg-slate-100 font-semibold" : "odd:bg-white even:bg-slate-50"}>
              {row.map((cell, ci) => (
                <td key={ci} className="border border-gray-100 px-2 py-1 whitespace-nowrap">
                  {cell ?? ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 200 && (
        <div className="p-2 text-[11px] text-gray-400 italic">Showing first 200 of {rows.length} rows.</div>
      )}
    </div>
  );
}

// Parses an xlsx/xls ArrayBuffer with SheetJS and renders the active sheet
// as a table, with tabs to switch sheets when there's more than one.
function SpreadsheetTable({ buffer, tall }) {
  const workbook = useMemo(() => {
    try {
      return XLSX.read(buffer, { type: "array" });
    } catch {
      return null;
    }
  }, [buffer]);
  const [activeSheet, setActiveSheet] = useState(0);

  if (!workbook || !workbook.SheetNames.length) {
    return <div className="p-4 text-xs text-gray-400 italic">Could not parse this spreadsheet.</div>;
  }

  const rows = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[activeSheet]], {
    header: 1,
    blankrows: false,
    defval: "",
  });

  return (
    <div>
      {workbook.SheetNames.length > 1 && (
        <div className="flex gap-1 px-2 pt-2 flex-wrap">
          {workbook.SheetNames.map((name, i) => (
            <button
              key={name}
              type="button"
              onClick={() => setActiveSheet(i)}
              className={`text-[11px] font-semibold px-2 py-1 rounded-md transition-colors ${
                i === activeSheet ? "bg-[#F05A19] text-white" : "bg-slate-100 text-gray-600 hover:bg-slate-200"
              }`}
            >
              {name}
            </button>
          ))}
        </div>
      )}
      <GridTable rows={rows} tall={tall} />
    </div>
  );
}

// Fetches a text-based file (csv/txt/json/md/log) and renders it inline.
function RemoteTextPreview({ text, parseAsCsv, tall }) {
  if (text === null) {
    return (
      <div className="p-4 text-xs text-gray-400 italic">
        Inline preview unavailable for this file — use Preview or Download below.
      </div>
    );
  }
  if (parseAsCsv) {
    return <GridTable rows={parseCSV(text)} tall={tall} />;
  }
  return <pre className={`${tall ? "max-h-[75vh]" : "max-h-72"} overflow-auto text-xs p-4 whitespace-pre-wrap bg-gray-50`}>{text}</pre>;
}

// Loads an uploaded submission file through the authenticated route as a
// blob, exposing it as a local object URL plus, for text-shaped files, its
// decoded text/array-buffer so the caller can render it directly.
function useAuthenticatedFile(submission) {
  const [state, setState] = useState({ loading: true, error: false, blobUrl: null, text: null, buffer: null });

  useEffect(() => {
    if (!submission?.file_download_url) {
      setState({ loading: false, error: true, blobUrl: null, text: null, buffer: null });
      return;
    }
    let cancelled = false;
    let objectUrl = null;
    setState({ loading: true, error: false, blobUrl: null, text: null, buffer: null });

    client
      .get(submission.file_download_url, { responseType: "blob" })
      .then(async ({ data: blob }) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        const ext = getFileExtension(submission.file_original_name);
        let text = null;
        let buffer = null;
        if (CSV_EXTENSIONS.includes(ext) || TEXT_EXTENSIONS.includes(ext)) {
          text = await blob.text();
        } else if (SPREADSHEET_EXTENSIONS.includes(ext)) {
          buffer = await blob.arrayBuffer();
        }
        if (!cancelled) setState({ loading: false, error: false, blobUrl: objectUrl, text, buffer });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, error: true, blobUrl: null, text: null, buffer: null });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submission?.file_download_url]);

  return state;
}

// Microsoft's Office viewer renders Word/Excel/PowerPoint faithfully (charts,
// images, formatting) but fetches the file itself, so it needs a public URL.
// Laravel mints a short-lived signed one for uploaded Office files only
// (GET /submissions/{id}/preview-link); the file is never public otherwise.
function useOfficeViewerUrl(submission, enabled) {
  const [state, setState] = useState({ loading: enabled, url: null, error: null });

  useEffect(() => {
    if (!enabled || !submission?.id) {
      setState({ loading: false, url: null, error: null });
      return;
    }
    let cancelled = false;
    setState({ loading: true, url: null, error: null });

    client
      .get(`/submissions/${submission.id}/preview-link`)
      .then(({ data }) => {
        if (cancelled) return;
        const src = encodeURIComponent(data.url);
        setState({ loading: false, url: `https://view.officeapps.live.com/op/embed.aspx?src=${src}`, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        // Say why, instead of silently falling back — otherwise a server-side
        // problem (route not deployed, bad file type…) just looks like "no viewer".
        const status = err?.response?.status;
        const reason = err?.response?.data?.message || err?.message || "request failed";
        setState({ loading: false, url: null, error: status ? `${reason} (HTTP ${status})` : reason });
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, submission?.id]);

  return state;
}

function PreviewFrame({ label, fileName, children, previewHref, downloadAction, showPreviewButton }) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      {children}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-slate-50">
        <div className="flex items-center gap-2 min-w-0">
          <PiFileTextBold className="text-gray-400 flex-shrink-0" size={18} />
          <span className="text-xs font-medium text-gray-600 truncate">{fileName}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {showPreviewButton && previewHref && (
            <a
              href={previewHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs font-semibold text-[#F05A19] bg-[#FEF0EB] hover:bg-[#FDD8CC] rounded-lg px-3 py-1.5 transition-colors"
            >
              <PiEyeBold size={14} /> Preview
            </a>
          )}
          <button
            type="button"
            onClick={downloadAction}
            className="flex items-center gap-1 text-xs font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-lg px-3 py-1.5 transition-colors"
          >
            <PiDownloadSimpleBold size={14} /> Download
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Uploaded file (auth-gated) ──────────────────────────────────────────────
function UploadedFilePreview({ submission, tall }) {
  const { loading, error, blobUrl, text, buffer } = useAuthenticatedFile(submission);
  const ext = getFileExtension(submission.file_original_name);
  const isSpreadsheet = SPREADSHEET_EXTENSIONS.includes(ext);
  const isOfficeDoc = OFFICE_DOC_EXTENSIONS.includes(ext);
  const office = useOfficeViewerUrl(submission, isSpreadsheet || isOfficeDoc);
  const [view, setView] = useState("office"); // spreadsheets: "office" (charts) | "table" (raw data)
  const fileName = submission.file_original_name || "attachment";
  const download = () =>
    downloadSubmissionFile(submission).catch(() => toast.error("Could not download this file."));

  if (loading) {
    return (
      <div className="border border-gray-200 rounded-xl p-4 text-xs text-gray-400 italic bg-white">
        Loading preview…
      </div>
    );
  }

  if (error || !blobUrl) {
    return (
      <PreviewFrame fileName={fileName} downloadAction={download} showPreviewButton={false}>
        <div className="p-4 text-xs text-gray-400 italic">
          Couldn't load a preview for this file — try Download below.
        </div>
      </PreviewFrame>
    );
  }

  const isImage = IMAGE_EXTENSIONS.includes(ext);
  const isPdf = PDF_EXTENSIONS.includes(ext);
  const isCsv = CSV_EXTENSIONS.includes(ext);
  const isText = TEXT_EXTENSIONS.includes(ext);

  // Office viewer when we have a link; a spreadsheet falls back to the data table
  // if the link couldn't be made or the user picks the table tab.
  const showOffice = !!office.url && (isOfficeDoc || view === "office");
  const showTable = isSpreadsheet && !!buffer && !office.loading && (!office.url || view === "table");

  return (
    <PreviewFrame fileName={fileName} downloadAction={download} showPreviewButton={isImage || isPdf}>
      {isImage && (
        <a href={blobUrl} target="_blank" rel="noopener noreferrer" className="block bg-gray-50">
          <img src={blobUrl} alt={fileName} className={`w-full ${tall ? "max-h-[80vh]" : "max-h-72"} object-contain`} />
        </a>
      )}
      {isPdf && <iframe title={fileName} src={blobUrl} className={`w-full ${tall ? "h-[80vh]" : "h-72"}`} />}
      {isSpreadsheet && !!office.url && (
        <div className="flex gap-1 px-2 pt-2 flex-wrap">
          {[
            ["office", "Document view (charts)"],
            ["table", "Data table"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setView(key)}
              className={`text-[11px] font-semibold px-2 py-1 rounded-md transition-colors ${
                view === key ? "bg-[#F05A19] text-white" : "bg-slate-100 text-gray-600 hover:bg-slate-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      {(isSpreadsheet || isOfficeDoc) && office.loading && (
        <div className="p-4 text-xs text-gray-400 italic">Preparing preview…</div>
      )}
      {showOffice && (
        <>
          <iframe title={fileName} src={office.url} className={`w-full ${tall ? "h-[80vh]" : "h-96"}`} />
          <p className="px-4 py-2 text-[11px] text-gray-400 border-t border-gray-100">
            Rendered by Microsoft Office Online. If it stays blank, use Download.
          </p>
        </>
      )}
      {!office.loading && !office.url && office.error && (isSpreadsheet || isOfficeDoc) && (
        <p className="mx-2 mt-2 rounded-md bg-amber-50 border border-amber-100 px-3 py-2 text-[11px] text-amber-700">
          Office preview unavailable — {office.error}.{isSpreadsheet ? " Showing the data table instead." : ""}
        </p>
      )}
      {showTable && <SpreadsheetTable buffer={buffer} tall={tall} />}
      {isCsv && <RemoteTextPreview text={text} parseAsCsv tall={tall} />}
      {isText && <RemoteTextPreview text={text} parseAsCsv={false} tall={tall} />}
      {isOfficeDoc && !office.loading && !office.url && (
        <div className="p-4 text-xs text-gray-400 italic">Use Download below to open this document.</div>
      )}
      {!isImage && !isPdf && !isSpreadsheet && !isCsv && !isText && !isOfficeDoc && (
        <div className="p-4 text-xs text-gray-400 italic">
          No inline preview available for this file type — use Download below.
        </div>
      )}
    </PreviewFrame>
  );
}

// ── Pasted external link (public URL) ───────────────────────────────────────
function LinkedFilePreview({ url }) {
  const ext = getFileExtension(url);
  const fileName = getFileName(url);
  const isImage = IMAGE_EXTENSIONS.includes(ext);
  const isPdf = PDF_EXTENSIONS.includes(ext);
  const isOfficeDoc = OFFICE_DOC_EXTENSIONS.includes(ext);
  const isSpreadsheet = SPREADSHEET_EXTENSIONS.includes(ext);
  const isCsv = CSV_EXTENSIONS.includes(ext);
  const isText = TEXT_EXTENSIONS.includes(ext);
  const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
  const previewHref = isOfficeDoc || isSpreadsheet ? officeViewerUrl : url;
  const download = () => window.open(url, "_blank", "noopener,noreferrer");

  const [textState, setTextState] = useState({ loading: isCsv || isText, text: null });
  const [sheetBuffer, setSheetBuffer] = useState(null);
  const [sheetLoading, setSheetLoading] = useState(isSpreadsheet);

  useEffect(() => {
    if (!isCsv && !isText) return;
    let cancelled = false;
    fetch(url)
      .then((res) => (res.ok ? res.text() : Promise.reject()))
      .then((t) => !cancelled && setTextState({ loading: false, text: t }))
      .catch(() => !cancelled && setTextState({ loading: false, text: null }));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => {
    if (!isSpreadsheet) return;
    let cancelled = false;
    fetch(url)
      .then((res) => (res.ok ? res.arrayBuffer() : Promise.reject()))
      .then((buf) => !cancelled && setSheetBuffer(buf))
      .catch(() => !cancelled && setSheetBuffer(null))
      .finally(() => !cancelled && setSheetLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return (
    <PreviewFrame
      fileName={fileName}
      downloadAction={download}
      previewHref={previewHref}
      showPreviewButton={isOfficeDoc}
    >
      {isImage && (
        <a href={url} target="_blank" rel="noopener noreferrer" className="block bg-gray-50">
          <img src={url} alt={fileName} className="w-full max-h-72 object-contain" />
        </a>
      )}
      {isPdf && <iframe title={fileName} src={url} className="w-full h-72" />}
      {isSpreadsheet && sheetLoading && (
        <div className="p-4 text-xs text-gray-400 italic">Loading preview…</div>
      )}
      {isSpreadsheet && !sheetLoading && sheetBuffer && <SpreadsheetTable buffer={sheetBuffer} />}
      {isSpreadsheet && !sheetLoading && !sheetBuffer && (
        // Cross-origin link didn't allow a direct fetch (no CORS) — fall back
        // to Microsoft's Office viewer, which fetches server-side instead.
        <iframe title={fileName} src={officeViewerUrl} className="w-full h-72" />
      )}
      {isCsv && (textState.loading
        ? <div className="p-4 text-xs text-gray-400 italic">Loading preview…</div>
        : <RemoteTextPreview text={textState.text} parseAsCsv />)}
      {isText && (textState.loading
        ? <div className="p-4 text-xs text-gray-400 italic">Loading preview…</div>
        : <RemoteTextPreview text={textState.text} parseAsCsv={false} />)}
      {isOfficeDoc && <iframe title={fileName} src={officeViewerUrl} className="w-full h-72" />}
      {!isImage && !isPdf && !isSpreadsheet && !isCsv && !isText && !isOfficeDoc && (
        <div className="p-4 text-xs text-gray-400 italic">
          No inline preview available for this file type — use Preview or Download below.
        </div>
      )}
    </PreviewFrame>
  );
}

// `tall` renders at near-full viewport height for the standalone viewer page.
export default function SubmissionFilePreview({ url, submission, tall = false }) {
  if (submission?.file_download_url) return <UploadedFilePreview submission={submission} tall={tall} />;
  if (url) return <LinkedFilePreview url={url} />;
  return null;
}
